import { Fragment, useState, useEffect } from "react";
import { bulkGenerateAndDownloadQR } from "../../components/shared/bulk-qr-generator";

import { Button, MediaQuery, Modal, Table } from "@mantine/core";
import { useSearchParams } from "react-router-dom";
import { FleetOperator } from "@captiva/shared-types";
import { FleetOperatorConfig } from "../../components/listings/configurations/assets/fleet-operator-config";
import { AssetEditor } from "../../components/shared/asset-editor";
import { FlexOptionsChoiceModal } from "./shared/flex-options-choice-modal";
import { Plus } from "tabler-icons-react";
import { useCreateFlexOptionsSet } from "../../hooks/useCreateFlexOptionsSet";
import TableRowActions from "./shared/table-row-actions";
import { BulkActionsMenu } from "./shared/bulk-actions-menu";

export interface AuthUser {
  uid: string;
  email?: string;
  claims?: {
    companyId?: string;
  };
}

interface VehicleOperatorsTableProps {
  elements: FleetOperator[];
  auth: AuthUser | null;
  reloadCallback: () => void;
}

export function VehicleOperatorsTable({ elements, auth, reloadCallback }: VehicleOperatorsTableProps) {
  async function handleBulkQRDownload() {
    await bulkGenerateAndDownloadQR({
      assetName: "Operator",
      items: elements,
      nameKey: "fullName",
      idKey: "id",
      zipName: "operators-qr-codes.zip",
      logoSrc: "/captiva-mark.png"
    });
  }
  const newDriver: Partial<FleetOperator> = {
    employeeCode: "",
    fullName: "",
    identification: "",
    nickName: "",
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [editedElement, setEditedElement] = useState<FleetOperator | Partial<FleetOperator> | undefined>(undefined);

  useEffect(() => {
    const editId = searchParams.get('editId');
    if (editId) {
      const match = elements.find((el) => el.id === editId);
      if (match) {
        setEditedElement(match);
      }
    }
  }, [searchParams, elements]);

  const closeEdit = () => {
    setEditedElement(undefined);
    if (searchParams.get('editId')) {
      searchParams.delete('editId');
      setSearchParams(searchParams, { replace: true });
    }
  };
  const [createFlexOptionsSet, { loading, notification, clearNotification, existingDocs, mergeOptions, createNewSet }] = useCreateFlexOptionsSet();
  
  const handleCreateFlexOptionsSet = () => {
    const companyId = auth?.claims?.companyId;
    if (!companyId) {
      alert("No company ID found in auth claims.");
      return;
    }
    createFlexOptionsSet({
      assetType: "Fleet Operator",
      assetList: elements,
      metadataPrefix: "fleet-operator",
      fieldMappings: {
        label: "fullName",
        value: "id",
        metadata: {
          employeeCode: "employeeCode",
          nickName: "nickName",
          identification: "identification",
          name: "fullName",
        }
      },
      companyId
    });
  };
  const rows = elements.map((element) => (
    <Fragment key={element.id}>
      {/* // desktop sizes */}
      <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>{element.fullName}</td>
          <td>{element.employeeCode}</td>
          <td>{element.nickName}</td>
          <td>
            <TableRowActions
              element={element}
              setEditedElement={setEditedElement}
              reloadCallback={reloadCallback}
              assetName="Operator"
              collectionName="fleetOperators"
              nameKey="fullName"
              idKey="id"
            />
          </td>
        </tr>
      </MediaQuery>
      {/* // phone sizes */}
      <MediaQuery largerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>{element.fullName}</td>
          <td>{element.employeeCode}</td>
          <td>
            <TableRowActions
              element={element}
              setEditedElement={setEditedElement}
              reloadCallback={reloadCallback}
              assetName="Operator"
              collectionName="fleetOperators"
              nameKey="fullName"
              idKey="id"
            />
          </td>
        </tr>
      </MediaQuery>
    </Fragment>
  ));

  return (
    <>
      {notification && notification.type !== "choice" && (
        <div style={{ position: "fixed", top: 76, right: 20, zIndex: 9999 }}>
          <Button color={notification.type === "success" ? "green" : "red"} variant="light" onClick={clearNotification}>
            {notification.message}
          </Button>
        </div>
      )}
      {elements.length > 0 && (
        <Table width="100%">
          <thead>
            <tr>
              <th>Name</th>
              <th>Employee code</th>
              <th>Nickname</th>
              <th style={{textAlign: 'left'}}>
                <BulkActionsMenu onBulkQRDownload={handleBulkQRDownload} onCreateFlexOptionsSet={handleCreateFlexOptionsSet} loading={loading} />
              </th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
      )}

      {editedElement && (
        <Modal opened onClose={closeEdit} title="Fleet operator">
          <AssetEditor
            asset={editedElement}
            assetConfig={FleetOperatorConfig}
            collection="fleetOperators"
            auth={auth}
            reloadCallback={() => {
              reloadCallback();
              closeEdit();
            }}
          ></AssetEditor>
        </Modal>
      )}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button
          onClick={() => setEditedElement(newDriver)}
          fullWidth
          style={{ maxWidth: 500 }}
          my="xl"
          color="cap-navy"
          variant="outline"
          leftIcon={<Plus />}
        >
          Create new
        </Button>
      </div>
      {notification && notification.type === "choice" && (
        <FlexOptionsChoiceModal
          opened={true}
          onClose={() => clearNotification()}
          onMerge={mergeOptions}
          onCreateNew={createNewSet}
          existingDocs={existingDocs}
          assetType="Fleet Operators"
        />
      )}
    </>
  );
}
